import { useRef, useEffect, useState } from "react";
import { getCertifications, getCertificationStats } from "@/api/homeApi";
import { readHomeCertificationsPreload, readHomeCertificationStatsPreload } from "@/utils/homePreload";

const certTypeLabels: Record<string, string> = { national: "国家级认证", industry: "行业资质", honor: "荣誉奖项" };
const certTypeColors: Record<string, string> = { national: "#1a56db", industry: "#0891b2", honor: "#d97706" };

function initialCertifications(): any[] {
  const list = readHomeCertificationsPreload();
  if (!Array.isArray(list)) return [];
  return list.map((c: any) => ({
    ...c,
    category: certTypeLabels[c.type] || c.category || c.type || "",
    color: c.color || certTypeColors[c.type] || "#6b7280",
    icon: c.icon || "ri-award-line",
    issuer: c.issuer || "",
    year: c.year || "",
  }));
}

export default function CertificationsSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [certifications, setCertifications] = useState<any[]>(initialCertifications);
  const [stats, setStats] = useState<any[]>(() => readHomeCertificationStatsPreload() ?? []);

  useEffect(() => {
    getCertifications().then((res: any) => {
      const list = Array.isArray(res) ? res : [];
      setCertifications(list.map((c: any) => ({
        ...c,
        category: certTypeLabels[c.type] || c.category || c.type || "",
        color: c.color || certTypeColors[c.type] || "#6b7280",
        icon: c.icon || "ri-award-line",
        issuer: c.issuer || "",
        year: c.year || "",
      })));
    }).catch(() => {});
    getCertificationStats().then((res: any) => setStats(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-10 lg:py-20 bg-[#f8fafc]" id="certifications">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
            <span className="text-[#1a56db] text-xs font-medium">权威认证</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0B1B3D] mb-4">
            欧格登实力经过<span className="text-[#1a56db]">权威机构</span>认可
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            ISO 27001、等保三级、CMMI 等多项国家级认证与行业资质，是欧格登智能专业能力与服务品质的最佳背书
          </p>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden mb-12">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`bg-white px-6 py-6 text-center transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="text-3xl lg:text-4xl font-bold text-[#0B1B3D] mb-1">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Certifications grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {certifications.map((cert, idx) => (
            <div
              key={cert.id}
              className={`group bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#1a56db]/20 hover:-translate-y-1 transition-all duration-500 cursor-default ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${200 + idx * 80}ms` }}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${cert.color}14` }}
                >
                  <i
                    className={`${cert.icon} text-xl`}
                    style={{ color: cert.color }}
                  ></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${cert.color}14`,
                        color: cert.color,
                      }}
                    >
                      {cert.category}
                    </span>
                    <span className="text-[10px] text-gray-300">{cert.year}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[#0B1B3D] mb-1 leading-snug">
                    {cert.name}
                  </h3>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <i className="ri-building-line text-[10px]"></i>
                    {cert.issuer}
                  </p>
                </div>

                {/* Check mark */}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-base text-emerald-400"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-emerald-400"></i>
            </div>
            所有证书均可提供原件核验
          </div>
          <div className="hidden sm:block w-px h-3 bg-gray-200"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line text-[#1a56db]"></i>
            </div>
            持续更新，保持行业领先资质
          </div>
          <div className="hidden sm:block w-px h-3 bg-gray-200"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-list-3-line text-gray-400"></i>
            </div>
            完整资质清单可联系商务获取
          </div>
        </div>
      </div>
    </section>
  );
}
